import {Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, QueryList, ViewChild, ViewChildren, ChangeDetectorRef} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { merge } from 'rxjs';
import { DateService } from 'src/app/service/date.service';
import { HeimvorteilGebaeudeart, HeimvorteilProduktart } from '../../../../shared/enum';
import { addRemoveFormArray, toggleEditMode } from '../../../../shared/utils';
import { CustomValidator } from '../../../../shared/validation';
import { IHvDaten } from '../../../models/hvdaten';
import { PopupService } from '../../../service/popup.service';
import { BewilligungsjahrHausComponent } from '../../bewilligungsjahr-haus/bewilligungsjahr-haus.component';
import { GebaeudeHochwasserzoneComponent } from '../../gebaeude-hochwasserzone/gebaeude-hochwasserzone.component';
import { NebengebaeudeComponent } from '../../nebengebaeude/nebengebaeude.component';
import { VerbauteFlaecheComponent } from '../../verbaute-flaeche/verbaute-flaeche.component';
import { VertragsbeginnLaufzeitComponent } from '../../vertragsbeginn-laufzeit/vertragsbeginn-laufzeit.component';
import {DataService} from '../../../service/data.service';

@Component({
  selector: 'app-calculatehv-daten',
  templateUrl: './calculatehv-daten.component.html',
  styleUrls: ['./calculatehv-daten.component.scss']
})
export class CalculateHvDatenComponent implements OnInit, OnChanges {

  @Input()
  parentFormGroupDirective: FormGroupDirective;

  @Input()
  formGroup: UntypedFormGroup;

  @Input()
  produktart: HeimvorteilProduktart;

  @Input()
  gebaeudeart: HeimvorteilGebaeudeart;

  @Input()
  showGroup = true;

  @Input()
  expertMode: boolean;

  @Output()
  calculateRatePendingEvent = new EventEmitter<void>();

  @Output()
  loadHvPaketePendingEvent = new EventEmitter<void>();

  @ViewChild(VertragsbeginnLaufzeitComponent) vertragsbeginnLaufzeitComponent: VertragsbeginnLaufzeitComponent;
  @ViewChild(VerbauteFlaecheComponent) verbauteFlaecheComponent: VerbauteFlaecheComponent;
  @ViewChild(BewilligungsjahrHausComponent) bewilligungsjahrHausComponent: BewilligungsjahrHausComponent;
  @ViewChild(GebaeudeHochwasserzoneComponent) gebaeudeHochwasserzoneComponent: GebaeudeHochwasserzoneComponent;
  @ViewChildren(NebengebaeudeComponent) nebengebaeudeComponents: QueryList<NebengebaeudeComponent>;

  constructor(private fb: UntypedFormBuilder, public translateService: TranslateService, public popupService: PopupService,
              private dateService: DateService, private ref: ChangeDetectorRef, public dataService: DataService) { }

  static createFormGroup(fb: UntypedFormBuilder) {
    return fb.group({
      vertragsbeginnLaufzeit: VertragsbeginnLaufzeitComponent.createFormGroup(fb),
      verbauteFlaeche: VerbauteFlaecheComponent.createFormGroup(fb),
      bauweise: new UntypedFormControl('', Validators.required),
      bewilligungsjahrHaus: BewilligungsjahrHausComponent.createFormGroup(fb),
      gebaeudeHochwasserzone: GebaeudeHochwasserzoneComponent.createFormGroup(fb),
      leitungswasserschaedenAusschliessen: new UntypedFormControl('', Validators.required),
      topEigenheim: new UntypedFormControl('', Validators.required),
      nebengebaeude: new UntypedFormControl('', Validators.required),
      anzahlNebengebaeude: new UntypedFormControl('', Validators.required),
      nebengebaeudeGroups: fb.array([]),
      wohnnutzFlaecheQm: new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9]*$'), Validators.min(1), Validators.max(200)]),
      einrichtung: new UntypedFormControl('', Validators.required),
      topHaushalt: new UntypedFormControl('', Validators.required),
      staendigBewohnt: new UntypedFormControl('', [Validators.required, CustomValidator.requiredYes]),
      wohnungGarten: new UntypedFormControl('', Validators.required)
    });
  }

  ngOnInit(): void {
    this.onValueChanged();
    this.toggleEditMode();

    this.vertragsbeginnLaufzeit['controls'].beginndatum.setValue(moment(this.dateService.today()));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.produktart && changes.produktart.currentValue !== changes.produktart.previousValue ||
      changes.gebaeudeart && changes.gebaeudeart.currentValue !== changes.gebaeudeart.previousValue) {
      this.toggleEditMode();
      if (this.nebengebaeude.value !== 'ja') {
        this.nebengebaeudeGroups.clear();
      }
    }
  }

  get vertragsbeginnLaufzeit() { return this.formGroup.get('vertragsbeginnLaufzeit'); }
  get verbauteFlaeche() { return this.formGroup.get('verbauteFlaeche'); }
  get bauweise() { return this.formGroup.get('bauweise'); }
  get bewilligungsjahrHaus() { return this.formGroup.get('bewilligungsjahrHaus'); }
  get gebaeudeHochwasserzone() { return this.formGroup.get('gebaeudeHochwasserzone'); }
  get leitungswasserschaedenAusschliessen() { return this.formGroup.get('leitungswasserschaedenAusschliessen'); }
  get topEigenheim() { return this.formGroup.get('topEigenheim'); }
  get nebengebaeude() { return this.formGroup.get('nebengebaeude'); }
  get anzahlNebengebaeude() { return this.formGroup.get('anzahlNebengebaeude'); }
  get nebengebaeudeGroups() { return this.formGroup.get('nebengebaeudeGroups') as UntypedFormArray; }
  get wohnnutzFlaecheQm() { return this.formGroup.get('wohnnutzFlaecheQm'); }
  get einrichtung() { return this.formGroup.get('einrichtung'); }
  get topHaushalt() { return this.formGroup.get('topHaushalt'); }
  get staendigBewohnt() { return this.formGroup.get('staendigBewohnt'); }
  get wohnungGarten() { return this.formGroup.get('wohnungGarten'); }

  get showAnzahlNebengebaeude() {
    return this.nebengebaeude.value === 'ja' ? true : false;
  }
  get showLeitungswasser() {
    return this.leitungswasserschaedenAusschliessen.value === 'nein' ? true : false;
  }
  get showTopEigenheim() {
    return this.topEigenheim.value === 'ja' ? true : false;
  }
  get showGebaeudeartWohnung() {
    return this.produktart === HeimvorteilProduktart.Haushalt &&
      this.gebaeudeart === HeimvorteilGebaeudeart.Wohnung ? true : false;
  }
  get showProduktHaushalt() {
    return this.produktart === HeimvorteilProduktart.Haushalt ||
      this.produktart === HeimvorteilProduktart.Buendel ? true : false;
  }
  get showProduktEigenheim() {
    return this.produktart === HeimvorteilProduktart.Eigenheim ||
      this.produktart === HeimvorteilProduktart.Buendel ? true : false;
  }
  get showProduktBuendel() {
    return this.produktart === HeimvorteilProduktart.Buendel ? true : false;
  }
  get isWohnung() {
    return this.produktart === 2 && this.gebaeudeart === 1;
  }

  toggleEditMode(): void {
    toggleEditMode(this.wohnnutzFlaecheQm, this.showGebaeudeartWohnung);
    toggleEditMode(this.einrichtung, this.showProduktHaushalt);
    toggleEditMode(this.topHaushalt, this.showProduktHaushalt);
    toggleEditMode(this.staendigBewohnt, this.showProduktHaushalt);
    toggleEditMode(this.wohnungGarten, this.showGebaeudeartWohnung);
    toggleEditMode(this.nebengebaeude, this.showProduktEigenheim);
    toggleEditMode(this.bauweise, this.showProduktEigenheim);
    toggleEditMode(this.leitungswasserschaedenAusschliessen, this.showProduktEigenheim);
    toggleEditMode(this.topEigenheim, this.showProduktEigenheim);
    toggleEditMode(this.anzahlNebengebaeude, this.showAnzahlNebengebaeude);
 }

  onValueChanged(): void {
    this.nebengebaeude.valueChanges.subscribe(() => {
      toggleEditMode(this.anzahlNebengebaeude, this.showAnzahlNebengebaeude);
      if (this.nebengebaeude.value === 'nein') {
        this.nebengebaeudeGroups.clear();
      } else {
        this.anzahlNebengebaeude.setValue('1');
      }
    });

    this.anzahlNebengebaeude.valueChanges.subscribe(() => {
      addRemoveFormArray(this.nebengebaeudeGroups, Number(this.anzahlNebengebaeude.value),
        (): UntypedFormGroup => { return NebengebaeudeComponent.createFormGroup(this.fb); });
    });
    merge(
      this.bauweise.valueChanges,
      this.leitungswasserschaedenAusschliessen.valueChanges,
      this.topEigenheim.valueChanges,
      this.wohnnutzFlaecheQm.valueChanges,
      this.einrichtung.valueChanges,
      this.topHaushalt.valueChanges,
      this.wohnungGarten.valueChanges
    ).subscribe(() => {
      this.calculateRatePendingEvent.emit();
    });
    merge(
      this.leitungswasserschaedenAusschliessen.valueChanges,
      this.topEigenheim.valueChanges,
      this.topHaushalt.valueChanges,
      this.wohnungGarten.valueChanges
    ).subscribe(() => {
      this.loadHvPaketePendingEvent.emit();
    });
  }

  getProduktName() {
    switch (this.produktart) {
      case HeimvorteilProduktart.Eigenheim:
        return 'Eigenheimversicherung';
      case HeimvorteilProduktart.Haushalt:
        return 'Haushaltsversicherung';
      default:
        return null;
    }
  }

  getProduktLink(produktart: number = undefined) {
    if (!produktart) { produktart = this.produktart; }
    switch (produktart) {
      case HeimvorteilProduktart.Eigenheim:
        return 'https://www.hdi.at/downloads/produktblaetter/eigenheim_2023.pdf';
      case HeimvorteilProduktart.Haushalt:
        return 'https://www.hdi.at/downloads/produktblaetter/haushalt_2023.pdf';
      default:
        return null;
    }
  }

  getDeckungsUmfangLink(produktart: number = undefined) {
    if(!produktart) { produktart = this.produktart; }
    switch (produktart) {
      case HeimvorteilProduktart.Eigenheim:
        return 'https://www.hdi.at/downloads/produktblaetter/deckungsumfang_eigenheim_2023.pdf';
      case HeimvorteilProduktart.Haushalt:
        return 'https://www.hdi.at/downloads/produktblaetter/deckungsumfang_haushalt_2023.pdf';
      default:
        return null;
    }
  }

  onCalculateRatePending() {
    this.calculateRatePendingEvent.emit();
  }

  onLoadHvPaketePendingEvent() {
    this.loadHvPaketePendingEvent.emit();
  }

  getDaten(hoechsthaftungssummeNebengebaeude: Array<number>): IHvDaten {
    return {
      ...this.vertragsbeginnLaufzeitComponent.getVertragsbeginnLaufzeit(),
      ...this.verbauteFlaecheComponent?.getVerbauteFlaeche(),
      BauweiseId: Number(this.bauweise.value),
      ...this.bewilligungsjahrHausComponent.getBewilligungsjahrHaus(),
      ...this.gebaeudeHochwasserzoneComponent.getGebaeudeHochwasserzone(),
      LeitungswasserschaedenAusschliessen: this.leitungswasserschaedenAusschliessen.value === 'ja'? true: false,
      TopEigenheim: this.topEigenheim.value === 'ja' ? true : false,
      Nebengebaeude: this.nebengebaeudeComponents.map((item, index) => (item.getNebengebaeude(hoechsthaftungssummeNebengebaeude[index]))),
      WohnnutzFlaecheQm: this.wohnnutzFlaecheQm.value,
      Einrichtung: Number(this.einrichtung.value),
      TopHaushalt: this.topHaushalt.value === 'ja' ? true : false,
      WohnungGarten: this.wohnungGarten.value ? this.wohnungGarten.value === 'ja' ? true : false : null
    } as IHvDaten;
  }

  setDaten(daten: IHvDaten): void{
    if(daten.Beginndatum) {
      this.vertragsbeginnLaufzeitComponent.setVertragsbeginnLaufzeit(daten);
    }
    this.verbauteFlaecheComponent?.setVerbauteFlaeche(daten);
    this.bauweise.setValue(daten.BauweiseId.toString());
    this.bewilligungsjahrHausComponent.setBewilligungsjahrHaus(daten);
    this.gebaeudeHochwasserzoneComponent.setGebaeudeHochwasserzone(daten);
    this.leitungswasserschaedenAusschliessen.setValue(daten.LeitungswasserschaedenAusschliessen? 'ja': 'nein');
    this.topEigenheim.setValue(daten.TopEigenheim != null ? daten.TopEigenheim === true ? 'ja' : 'nein' : null);
    this.topHaushalt.setValue(daten.TopHaushalt != null ? daten.TopHaushalt === true ? 'ja' : 'nein' : null);
    this.wohnnutzFlaecheQm.setValue(daten.WohnnutzFlaecheQm);
    this.einrichtung.setValue(daten.Einrichtung.toString());
    this.wohnungGarten.setValue(daten.WohnungGarten != null ? daten.WohnungGarten === true ? 'ja' : 'nein' : null);
    this.staendigBewohnt.setValue('ja');
    this.nebengebaeude.setValue(daten.Nebengebaeude.length > 0? 'ja': 'nein');
    this.anzahlNebengebaeude.setValue(daten.Nebengebaeude.length.toString());
    this.ref.detectChanges();
    for (let i = 0; i < daten.Nebengebaeude.length; i++) {
      this.nebengebaeudeComponents.toArray()[i].setNebengebaeude(daten.Nebengebaeude[i]);
    }
  }

  get Einrichtungen(){
    return this.dataService.hvEinrichtungen;
  }
}
